import _ from "underscore";

import Application from "shared/application";
import ExceptionHandling from "shared/logging/exception_handling";

export const renderMoney = amount => {
  const amountString = amount === null ? "" : amount.toString().trim();

  if (amountString === "") {
    return "-";
  }

  const isNegative = amountString[0] === "-";
  const absoluteAmount = isNegative ? amountString.substr(1) : amountString;
  let prefix = "";
  if (isNegative) prefix = "-";
  return `${prefix}${Application.currency}${formatDecimal(absoluteAmount)}`;
};

export const pluralize = (count, singular, includeCount = false) =>
  (includeCount ? count + " " : "") + (count === 1 ? singular : singular + "s");

export const camelize = string =>
  string.replace(/[-_\s]+(.)?/g, (match, c) => (c ? c.toUpperCase() : ""));

export const capitalize = string => string.charAt(0).toUpperCase() + string.slice(1);

export const classify = string =>
  capitalize(camelize(string.replace(/[\W_]/g, " ")).replace(/\s/g, ""));

/**
 * formatDecimal
 *
 * This function is used to format all numeric values into localized and non-localized formats.
 *
 * @param value The value you want to format
 * @param options Object of attributes that can be passed as configuration options
 * @returns {string}
 */
export const formatDecimal = (value, options) => {
  const config = {
    decimalPrecision: 2,
    preserveDecimals: false,
    addCommas: true,
    inputDecimalCharacter: ".",
    trimDecimalIfZero: true,
    ...options
  };

  let ps = parseFloat(value);
  if (!config.preserveDecimals) {
    ps = ps.toFixed(config.decimalPrecision);
  }
  ps = ps.toString().split(config.inputDecimalCharacter);

  let whole = ps[0];
  const sub = ps.length > 1 ? ps[1] : "";
  const r = /(\d+)(\d{3})/;

  if (config.addCommas) {
    while (r.test(whole)) {
      whole = whole.replace(r, "$1" + "," + "$2");
    }
  }

  if (config.trimDecimalIfZero && (sub === "" || Number(sub) === 0)) {
    return whole;
  } else if (
    (config.decimalPrecision > 0 && !config.preserveDecimals) ||
    (config.preserveDecimals && sub !== "")
  ) {
    return whole + "." + sub;
  }
  return whole;
};

export const correctDecimals = (amount, precision) => {
  const v = parseFloat(amount).toFixed(
    precision !== null && precision !== undefined ? precision : 2
  );
  const ps = v.split(".");

  const whole = ps[0];
  const sub = ps[1] ? "." + ps[1] : ".00";

  return whole + sub;
};

export const zeroPad = (num, width) => {
  if (width > 10) {
    ExceptionHandling.logError(
      "width greater than 10 not supported in zero_pad function: " + width
    );
    return num;
  }

  return ("0000000000" + Math.round(num)).slice(-width);
};

/**
 * Helper method, adds HTML (a span) around the value to indicate trends or differences.
 * @param val
 * @param formattedValue
 * @returns {string} Returns an HTML string with
 */
export const addTrendColorAroundValue = (val, formattedValue) => {
  formattedValue = typeof formattedValue === "undefined" ? val : formattedValue;

  return '<span style="color: ' + getColorByValue(val) + '">' + formattedValue + "</span>";
};

/**
 * Helper method to determine color for trend values.
 * @param val
 * @returns {string} Returns the hex value for the color
 */
export const getColorByValue = val => {
  let color;

  if (parseFloat(val) < 0) {
    color = "#990000";
  } else if (val === 0) {
    color = "#333";
  } else {
    color = "#009900";
  }

  return color;
};

/**
 * A helper function that determines if a column is a trend or difference indicator column.
 *
 * @param (string) columnName The name of a column.
 * @returns (boolean) Returns true if the word "trend" or "difference" appears in the column name. Otherwise returns false.
 */
export const isTrendOrDifferenceColumn = columnName => {
  return columnName.match(/\.(trend|difference)/);
};

export const formatSeconds = (seconds, config) => {
  if (seconds === null || seconds === "") {
    return "0:00&nbsp;";
  }

  const prefix = seconds < 0 ? "-" : "";
  let positiveSeconds = Math.round(seconds);

  if (seconds < 0) {
    positiveSeconds = -1.0 * seconds;
  }

  const hourMin = Math.floor(positiveSeconds / 60);
  const sec = positiveSeconds % 60;
  const hour = Math.floor(hourMin / 60);
  const min = hourMin % 60;
  let output = "";

  if (hourMin >= 60) {
    output = prefix + hour + ":" + zeroPad(min, 2) + ":" + zeroPad(sec, 2) + "&nbsp;";
  } else {
    output = prefix + min + ":" + zeroPad(sec, 2) + "&nbsp;";
  }

  if (isTrendOrDifferenceColumn(config.attr)) {
    return addTrendColorAroundValue(seconds, output);
  } else {
    return output;
  }
};

/**
 * A helper function that removes whitespace from url host and encodes whitespaces within url path and query params.
 *
 * @param (string) string to be processed.
 * @returns (string) the processed string.
 */
export const sanitizeUrl = string => {
  var url = string.trim().replace(/\/\s+\//g, "//");
  // remove spaces from url but not from substitution params
  url = url
    .replace(/\s+(?![^<>]*>)/g, "")
    .replace(/<\s+/g, "<")
    .replace(/\s+>/g, ">");

  return url;
};

/**
 * A helper function that cleans HTTP header lines by removing whitespace from header names and leaving one consecutive space in the header values.
 *
 * @param (string) string to be processed.
 * @returns (string) the processed string.
 */
export const cleanupHeaders = string => {
  var lines = string.trim().split("\n");
  for (var i = 0; i < lines.length; i++) {
    var header = lines[i].trim().split(":");
    lines[i] = header.shift().replace(/\s+/g, "");
    if (header.length > 0) {
      lines[i] += ":" + header.join(":");
    }
  }

  return lines.join("\n").replace(/\n{2,}/g, "\n");
};

/**
 * A helper function that removes redundant whitespace, ampersands (&), and "extra" newlines (leaves 1 per row).
 *
 * @param (string) string to be processed.
 * @param (boolean) removeAmpersands.
 * @returns (string) the processed string.
 * keep whitespace within names and values
 * Useful for cleaning up and validating tracking pixel parameters XML & HTTP
 */
export const cleanupParams = (string, removeAmpersands) => {
  var lines = string.trim().split("\n");
  for (var i = 0; i < lines.length; i++) {
    if (removeAmpersands) {
      lines[i] = lines[i].replace(/&+/g, "");
    }
    // keep spaces inside name/value pairs
    lines[i] = lines[i]
      .replace(/\s*=\s*/, "=")
      .replace(/\s+(?![^<>]*>)/g, " ")
      .replace(/<\s+/g, "<")
      .replace(/\s+>/g, ">");
  }
  return lines.join("\n").replace(/\n{2,}/g, "\n");
};

/**
 * asBoolean
 *
 * This function is used to convert string values into a boolean value.
 *
 * @param (string) string to be processed.
 * @returns (boolean) the equivalent boolean value for the string.
 */

export const asBoolean = string => {
  if (_.isUndefined(string)) {
    return false;
  } else {
    string = string.toString();
    if (string.toLowerCase() === "true") {
      return true;
    } else if (string.toLowerCase() === "false") {
      return false;
    } else if (Number(string)) {
      return !!parseInt(string);
    } else {
      return Boolean(string);
    }
  }
};
